import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Building2, Users, Shield, CheckCircle2, ArrowRight, Heart, Pill, TrendingUp, FileCheck, Handshake, Clock, Phone, DollarSign, HeartHandshake, Scale, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const serviceIcons = [FileCheck, Phone, DollarSign, HeartHandshake];
const programIcons = [Shield, Heart, Pill, DollarSign];
const benefitIcons = [TrendingUp, Clock, Heart, Handshake];
const b340Icons = [Users, FileCheck, TrendingUp];
const trustIcons = [Users, Heart, Scale, Shield, FileCheck];

export const CommunityOrganizationsPage = () => {
  const { t } = useTranslation();
  const services = t('community.services', { returnObjects: true });
  const programSupport = t('community.programs', { returnObjects: true });
  const whoWeServe = t('community.serveList', { returnObjects: true });
  const benefits = t('community.benefits', { returnObjects: true });
  const b340Cards = t('community.b340Cards', { returnObjects: true });
  const trustCards = t('community.trustCards', { returnObjects: true });

  return (
    <div className="overflow-hidden" data-testid="community-organizations-page">
      <section className="bg-slate-900 py-20 lg:py-28" data-testid="co-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              <Building2 className="h-4 w-4" />
              {t('community.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
              {t('community.heroTitlePre')}{' '}<span className="text-sky-400">{t('community.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-300 mb-4 max-w-3xl mx-auto">{t('community.heroDesc')}</p>
            <p className="text-base text-slate-400 mb-8">{t('community.heroServing')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="co-hero-cta">
                  {t('community.heroCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 h-12 px-8 rounded-full" data-testid="co-hero-contact">{t('community.heroConsult')}</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-sky-500" data-testid="co-value">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <TrendingUp className="h-8 w-8 mx-auto mb-3 opacity-90" />
              <h3 className="font-semibold text-lg">{t('community.value1Title')}</h3>
              <p className="text-sky-100 text-sm">{t('community.value1Sub')}</p>
            </div>
            <div>
              <Clock className="h-8 w-8 mx-auto mb-3 opacity-90" />
              <h3 className="font-semibold text-lg">{t('community.value2Title')}</h3>
              <p className="text-sky-100 text-sm">{t('community.value2Sub')}</p>
            </div>
            <div>
              <Users className="h-8 w-8 mx-auto mb-3 opacity-90" />
              <h3 className="font-semibold text-lg">{t('community.value3Title')}</h3>
              <p className="text-sky-100 text-sm">{t('community.value3Sub')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="co-services">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('community.servicesTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('community.servicesSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = serviceIcons[index] || FileCheck;
              return (
                <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-8 rounded-2xl border border-slate-200 card-shadow" data-testid={`service-card-${index}`}>
                  <div className="h-14 w-14 bg-slate-900 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-12 bg-sky-50 border border-sky-100 rounded-2xl p-8">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('community.adminTitle')}</h3>
              <p className="text-slate-700 text-lg italic">{t('community.adminQuote')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="co-programs">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('community.programsTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('community.programsSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programSupport.map((program, index) => {
              const Icon = programIcons[index] || Shield;
              return (
                <motion.div key={program.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow text-center" data-testid={`program-card-${index}`}>
                  <div className="h-12 w-12 bg-sky-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-6 w-6 text-sky-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{program.title}</h3>
                  <p className="text-slate-600 text-sm">{program.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="co-serve">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('community.serveTitle')}</h2>
              <p className="text-lg text-slate-600 mb-8">{t('community.serveText')}</p>
              <div className="grid grid-cols-1 gap-3">
                {whoWeServe.map((org) => (
                  <div key={org} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 flex-shrink-0" />
                    <span className="text-slate-700">{org}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">{t('community.whyTitle')}</h3>
              <div className="space-y-6">
                {benefits.map((benefit, idx) => {
                  const Icon = benefitIcons[idx] || TrendingUp;
                  return (
                    <div key={benefit.title} className="flex items-start gap-4">
                      <div className="h-10 w-10 bg-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-sky-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">{benefit.title}</h4>
                        <p className="text-slate-400 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="co-340b">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="h-4 w-4" />
                {t('community.b340Badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('community.b340Title')}</h2>
              <p className="text-lg text-slate-600">{t('community.b340Text')}</p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6">
              {b340Cards.map((card, index) => {
                const Icon = b340Icons[index] || Users;
                return (
                  <div key={card.title} className="bg-white p-6 rounded-xl border border-slate-100 card-shadow text-center">
                    <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">{card.title}</h4>
                    <p className="text-slate-600 text-sm">{card.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="co-trust">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
                <ShieldCheck className="h-4 w-4" />
                {t('community.trustBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('community.trustTitle')}</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trustCards.map((card, index) => {
                const Icon = trustIcons[index] || Users;
                return (
                  <motion.div key={card.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className={`bg-slate-50 p-6 rounded-xl ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
                    <div className="h-10 w-10 bg-sky-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-5 w-5 text-sky-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">{card.title}</h4>
                    <p className="text-slate-600 text-sm">{card.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900" data-testid="co-compliance">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
                <Scale className="h-4 w-4" />
                {t('community.complianceBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('community.complianceTitle')}</h2>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto">{t('community.complianceText')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900" data-testid="co-cta">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('community.ctaTitle')}</h2>
              <p className="text-slate-300 text-lg mb-8">{t('community.ctaText')}</p>
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="co-final-cta">
                  {t('community.ctaBtn')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-slate-50 border-t border-slate-100" data-testid="co-disclaimer">
        <div className="container-custom">
          <p className="text-slate-500 text-xs text-center max-w-4xl mx-auto">{t('community.disclaimer')}</p>
        </div>
      </section>
    </div>
  );
};

export default CommunityOrganizationsPage;
